import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard, Search, Loader2, Eye, Plus, Ban, RotateCcw, Download,
  CheckCircle2, FileSpreadsheet, FileText, Calendar, User as UserIcon, Mail,
  TrendingUp, Wallet, AlertTriangle, Clock, Phone, MessageCircle, PlayCircle,
  CalendarPlus, FileSignature, Upload, X, Paperclip, Pencil, MessageSquare,
  Check, XCircle, History,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

import { exportToPDF } from "../../lib/export";
import { API_URL } from "../../config";

const STATUS_META = {
  active: { label: "Em curso", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  completed: { label: "Concluído", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  expired: { label: "Expirado", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  overdue: { label: "Em atraso", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  frozen: { label: "Congelado", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
};

const fmtMoney = (n) => `${(n || 0).toFixed(2)}€`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-PT"); } catch { return iso; }
};

export const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detailPlan, setDetailPlan] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: open a specific plan via ?open=<registration_id>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setDetailPlan({ id: openId });
      // Clean the URL so the modal doesn't reopen after closing
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { data } = await axios.get(`${API_URL}/api/admin/installment-plans${params}`, { withCredentials: true });
      setPlans(data.plans || []);
      setSummary(data.summary);
    } catch (err) {
      toast.error("Erro ao carregar planos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
        const { data } = await axios.get(`${API_URL}/api/admin/installment-plans${params}`, { withCredentials: true });
        if (!cancelled) { setPlans(data.plans || []); setSummary(data.summary); }
      } catch (err) {
        if (!cancelled) toast.error("Erro ao carregar planos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const _s = (search || "").trim().toLowerCase();
  const filtered = _s
    ? plans.filter((p) =>
        (p.email || "").toLowerCase().includes(_s) ||
        (p.first_name || "").toLowerCase().includes(_s) ||
        (p.last_name || "").toLowerCase().includes(_s) ||
        (p.ticket_number || "").toLowerCase().includes(_s) ||
        (p.phone || "").toLowerCase().includes(_s)
      )
    : plans;

  const rowsForExport = filtered.map((p) => ({
    "Bilhete": p.ticket_number,
    "Comprador": `${p.first_name || ""} ${p.last_name || ""}`.trim(),
    "Email": p.email,
    "Tipo": p.ticket_type_name,
    "Sessão": `${p.session_date || ""} ${p.session_time || ""}`.trim(),
    "Estado": STATUS_META[p.plan_status]?.label || p.plan_status,
    "Total (€)": (p.total_due || 0).toFixed(2),
    "Pago (€)": (p.total_paid || 0).toFixed(2),
    "Restante (€)": (p.remaining_due || 0).toFixed(2),
    "%": `${p.progress_percent}%`,
    "Prestações": p.installments_count,
    "Criado": fmtDate(p.created_at),
  }));

  const exportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(rowsForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Planos");
    XLSX.writeFile(wb, `planos_pagamento_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = () => {
    const columns = Object.keys(rowsForExport[0] || {}).map((k) => ({ header: k, key: k }));
    exportToPDF(rowsForExport, columns, `Planos de Pagamento (${rowsForExport.length})`, `planos_${new Date().toISOString().slice(0, 10)}`);
  };

  return (
    <div data-testid="plans-page">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Planos</h1>
          <p className="text-zinc-500 text-sm mt-1">Gerir os Planos de Pagamento: prestações, registar pagamentos offline, cancelar planos.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="input-dark w-40" data-testid="plans-status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10 text-white">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Em curso</SelectItem>
              <SelectItem value="overdue">Em atraso</SelectItem>
              <SelectItem value="completed">Concluídos</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
              <SelectItem value="frozen">Congelados</SelectItem>
              <SelectItem value="expired">Expirados</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar..." className="input-dark pl-10 w-64" data-testid="plans-search" />
          </div>
          <Button onClick={exportXlsx} variant="outline" className="border-white/10 text-zinc-200" data-testid="plans-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</Button>
          <Button onClick={exportPdf} variant="outline" className="border-white/10 text-zinc-200" data-testid="plans-export-pdf"><FileText className="w-4 h-4 mr-2" /> PDF</Button>
          <Button
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API_URL}/api/admin/installment-plans/run-sweeper`, {}, { withCredentials: true });
                const s = data.stats || {};
                toast.success(`Sweeper: ${s.plans_seen || 0} planos · ${s.cancelled || 0} cancelados · ${s.frozen || 0} congelados · ${s.flagged || 0} flagged · ${s.reminders_sent || 0} lembretes`);
                load();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro no sweeper");
              }
            }}
            variant="outline"
            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
            data-testid="plans-run-sweeper"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> Sweeper
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><CreditCard className="w-3.5 h-3.5" />Planos</div>
            <p className="text-2xl font-bold text-white mt-1">{summary.count}</p>
          </div>
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><TrendingUp className="w-3.5 h-3.5" />Total Devido</div>
            <p className="text-2xl font-bold text-white mt-1">{fmtMoney(summary.total_due)}</p>
          </div>
          <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-wider"><Wallet className="w-3.5 h-3.5" />Recebido</div>
            <p className="text-2xl font-bold text-emerald-300 mt-1">{fmtMoney(summary.total_paid)}</p>
          </div>
          <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-wider"><AlertTriangle className="w-3.5 h-3.5" />A Receber</div>
            <p className="text-2xl font-bold text-amber-300 mt-1">{fmtMoney(summary.total_remaining)}</p>
          </div>
        </div>
      )}

      <OpenRequestsWidget onOpenPlan={(planId) => setDetailPlan({ id: planId })} />
      <OverdueClientsWidget onOpenPlan={(planId) => setDetailPlan({ id: planId })} />

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm" data-testid="plans-empty">
            {search || statusFilter !== "all" ? "Nenhum plano corresponde aos filtros." : "Ainda não há planos de pagamento."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="plans-table">
              <thead className="bg-black/40 text-zinc-400 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Bilhete</th>
                  <th className="px-4 py-3 text-left">Comprador</th>
                  <th className="px-4 py-3 text-left">Sessão</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Pago</th>
                  <th className="px-4 py-3 text-right">Restante</th>
                  <th className="px-4 py-3 text-center">Progresso</th>
                  <th className="px-4 py-3 text-center">Acções</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const meta = STATUS_META[p.plan_status] || STATUS_META.active;
                  return (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`plan-row-${p.id}`}>
                      <td className="px-4 py-3 font-mono text-amber-400">{p.ticket_number}</td>
                      <td className="px-4 py-3">
                        <p className="text-white text-sm">{`${p.first_name || ""} ${p.last_name || ""}`.trim() || "—"}</p>
                        <p className="text-xs text-zinc-500">{p.email}</p>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 text-xs">{p.session_date || "—"}<br /><span className="text-zinc-500">{p.session_time || ""}</span></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${meta.cls}`}>{meta.label}</span></td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmtMoney(p.total_due)}</td>
                      <td className="px-4 py-3 text-right text-emerald-300">{fmtMoney(p.total_paid)}</td>
                      <td className="px-4 py-3 text-right text-amber-300">{fmtMoney(p.remaining_due)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 bg-white/5 rounded-full h-1.5 overflow-hidden">
                            <div className="h-full bg-amber-400" style={{ width: `${Math.min(100, p.progress_percent)}%` }} />
                          </div>
                          <span className="text-xs text-zinc-400">{p.progress_percent}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => setDetailPlan(p)} className="p-1.5 text-zinc-400 hover:text-amber-300 hover:bg-amber-500/10 rounded" title="Ver detalhe" data-testid={`plan-view-${p.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailPlan && (
        <PlanDetailDialog
          planId={detailPlan.id}
          open={!!detailPlan}
          onClose={() => setDetailPlan(null)}
          onChanged={() => { setDetailPlan(null); load(); }}
        />
      )}
    </div>
  );
};

/**
 * Sprint 6 — Widget for the Plans page: top overdue plans with quick
 * actions (WhatsApp, Ver plano).
 */
const OpenRequestsWidget = ({ onOpenPlan }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/installment-plans-open-requests?limit=10`, { withCredentials: true });
      setItems(data.requests || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return null;
  if (items.length === 0) return null;

  const pending = items.filter((r) => r.status === "pending").length;
  const countered = items.filter((r) => r.status === "countered").length;
  const fmt = (iso) => iso ? new Date(iso + (iso.length === 10 ? "T00:00:00Z" : "")).toLocaleDateString("pt-PT") : "—";

  return (
    <div className="bg-gradient-to-br from-amber-950/40 to-orange-950/20 border border-amber-500/30 rounded-xl p-4 mb-4" data-testid="open-requests-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <p className="text-amber-100 font-semibold">Pedidos de alteração em aberto</p>
            <p className="text-xs text-zinc-400">
              {pending > 0 && <span>{pending} a aguardar decisão</span>}
              {pending > 0 && countered > 0 && " · "}
              {countered > 0 && <span>{countered} com contra-proposta aguardando comprador</span>}
            </p>
          </div>
        </div>
        <span className="text-2xl font-bold text-amber-300">{items.length}</span>
      </div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {items.map((r) => (
          <button
            key={r.request_id}
            type="button"
            onClick={() => onOpenPlan(r.registration_id)}
            className={`w-full text-left rounded-lg border px-3 py-2 hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2 ${
              r.status === "countered" ? "border-sky-500/30 bg-sky-500/5" : "border-amber-500/25 bg-amber-500/5"
            }`}
            data-testid={`open-request-${r.request_id}`}
          >
            <div className="min-w-0">
              <p className="text-sm text-white truncate">
                <span className="font-mono text-amber-300">{r.ticket_number}</span> · {r.buyer_name || r.buyer_email}
              </p>
              <p className="text-xs text-zinc-400 truncate">
                Prestação #{r.sequence}: {r.proposed_date && <>data → <strong>{fmt(r.proposed_date)}</strong></>}
                {r.proposed_amount != null && <>{r.proposed_date ? " · " : ""}valor → <strong>{Number(r.proposed_amount).toFixed(2)}€</strong></>}
              </p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
              r.status === "countered" ? "bg-sky-500/20 text-sky-200" : "bg-amber-500/20 text-amber-200"
            }`}>
              {r.status === "countered" ? "Contra-proposta" : "A responder"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};


const OverdueClientsWidget = ({ onOpenPlan }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/installment-plans-overdue?limit=10`, { withCredentials: true });
      setItems(data.plans || []);
    } catch {
      // fail silently — widget is non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/installment-plans-overdue?limit=10`, { withCredentials: true });
        if (!cancelled) setItems(data.plans || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-orange-500/20 rounded-xl p-4 mb-6" data-testid="overdue-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-zinc-900 border border-emerald-500/20 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="overdue-widget-empty">
        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        <p className="text-sm text-zinc-300">Sem clientes em atraso. <span className="text-zinc-500">Excelente!</span></p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-950/40 to-red-950/20 border border-orange-500/30 rounded-xl p-4 mb-6" data-testid="overdue-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-400" />
          <h3 className="text-orange-100 font-semibold">Top Clientes em Atraso</h3>
          <span className="bg-orange-500/20 text-orange-300 text-xs px-2 py-0.5 rounded-full border border-orange-500/30">{items.length}</span>
        </div>
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="text-orange-300 hover:text-orange-100 text-xs"
          data-testid="overdue-widget-toggle"
        >
          {collapsed ? "Mostrar" : "Esconder"}
        </button>
      </div>
      {!collapsed && (
        <div className="space-y-1.5">
          {items.map((p) => {
            const fullName = `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email;
            const phoneClean = (p.phone || "").replace(/[^0-9+]/g, "");
            const waMsg = encodeURIComponent(
              `Olá ${p.first_name || ""}, falamos do Bilhetez. ` +
              `Reparámos que o pagamento do seu plano ${p.ticket_number} (sessão ${p.session_date || ""}) ` +
              `está em atraso há ${p.days_overdue} dia(s). ` +
              `Restam ${(p.remaining_due || 0).toFixed(2)}€ por pagar. Aceda à sua conta para regularizar: https://bilhetez.com/conta`
            );
            const waLink = phoneClean ? `https://wa.me/${phoneClean.replace("+", "")}?text=${waMsg}` : null;
            return (
              <div
                key={p.id}
                className="flex items-center gap-3 bg-black/20 hover:bg-black/30 rounded-lg px-3 py-2 transition-colors"
                data-testid={`overdue-row-${p.id}`}
              >
                <div className="flex-shrink-0 text-center w-12">
                  <p className="text-orange-300 font-bold text-lg leading-tight">{p.days_overdue}</p>
                  <p className="text-orange-500/70 text-[10px] uppercase tracking-wider">dia(s)</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate font-medium">{fullName}</p>
                  <p className="text-zinc-400 text-xs truncate">
                    <span className="font-mono text-amber-300/80">{p.ticket_number}</span>
                    <span className="mx-1.5">·</span>
                    {p.session_date} {p.session_time}
                    <span className="mx-1.5">·</span>
                    <span className="text-amber-300">{(p.remaining_due || 0).toFixed(2)}€ em falta</span>
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {waLink ? (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 hover:text-emerald-100"
                      title="Enviar WhatsApp"
                      data-testid={`overdue-whatsapp-${p.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  ) : (
                    <span className="p-2 rounded-md bg-zinc-800/50 text-zinc-600" title="Sem telefone">
                      <Phone className="w-4 h-4" />
                    </span>
                  )}
                  <a
                    href={`mailto:${p.email}?subject=${encodeURIComponent("Pagamento em atraso — " + (p.ticket_number || ""))}`}
                    className="p-2 rounded-md bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 hover:text-sky-100"
                    title="Enviar email"
                    data-testid={`overdue-email-${p.id}`}
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => onOpenPlan && onOpenPlan(p.id)}
                    className="p-2 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 hover:text-amber-100"
                    title="Ver plano"
                    data-testid={`overdue-view-${p.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};




const PlanDetailDialog = ({ planId, open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [editingScheduleItem, setEditingScheduleItem] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!planId) return;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API_URL}/api/admin/installment-plans/${planId}`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) toast.error("Erro ao carregar plano");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [planId]);

  const reload = async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/installment-plans/${planId}`, { withCredentials: true });
      setData(d);
    } catch { /* noop */ }
  };

  if (loading || !data) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader className="sr-only">
            <DialogTitle>A carregar plano</DialogTitle>
            <DialogDescription>A obter detalhes do plano de pagamento.</DialogDescription>
          </DialogHeader>
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" /></div>
        </DialogContent>
      </Dialog>
    );
  }

  const plan = data.plan;
  const total = plan.total_due || plan.final_price || 0;
  const paid = plan.total_paid || 0;
  const remaining = Math.max(0, total - paid);
  const rsum = plan.refund_summary;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-400" /> Plano · {plan.ticket_number}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {`${plan.first_name || ""} ${plan.last_name || ""}`.trim()} · {plan.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/5 rounded-lg p-3"><p className="text-[10px] uppercase text-zinc-500">Total</p><p className="text-xl font-bold text-white">{fmtMoney(total)}</p></div>
            <div className="bg-emerald-500/10 rounded-lg p-3"><p className="text-[10px] uppercase text-zinc-500">Pago</p><p className="text-xl font-bold text-emerald-300">{fmtMoney(paid)}</p></div>
            <div className="bg-amber-500/10 rounded-lg p-3"><p className="text-[10px] uppercase text-zinc-500">Restante</p><p className="text-xl font-bold text-amber-300">{fmtMoney(remaining)}</p></div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-zinc-300 mb-2">Histórico de prestações ({(plan.installments || []).length})</h4>
            {(plan.installments || []).length === 0 ? (
              <p className="text-xs text-zinc-500 bg-white/[0.02] rounded p-3">Sem prestações registadas.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {plan.installments.slice().reverse().map((inst) => (
                  <div key={inst.id} className="flex items-center justify-between text-sm bg-white/[0.03] rounded px-3 py-2">
                    <div>
                      <p className="text-zinc-200">{new Date(inst.paid_at).toLocaleString("pt-PT")}</p>
                      <p className="text-xs text-zinc-500">{inst.payment_method}{inst.note ? ` · ${inst.note}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-300 font-mono">+{fmtMoney(inst.amount)}</span>
                      {inst.payment_method !== "stripe" && plan.plan_status !== "cancelled" && (
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(`${API_URL}/api/admin/installment-plans/${plan.id}/payments/${inst.id}`, { withCredentials: true });
                              toast.success("Prestação removida");
                              await reload();
                              onChanged && onChanged();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Erro");
                            }
                          }}
                          className="text-red-300 hover:text-red-200 text-xs underline"
                          title="Remover prestação manual"
                          data-testid={`plan-installment-delete-${inst.id}`}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {rsum && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-red-200 mb-2">Resumo de Cancelamento</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-zinc-400">Política:</span> <span className="text-white">{rsum.policy}</span></div>
                <div><span className="text-zinc-400">A reembolsar:</span> <span className="text-amber-300">{fmtMoney(rsum.refund_total)}</span></div>
                <div><span className="text-zinc-400">Stripe pendente:</span> <span className="text-white">{fmtMoney((rsum.stripe_refund_due || 0) - (rsum.stripe_refunded_amount || 0))}</span></div>
                <div><span className="text-zinc-400">Manual pendente:</span> <span className="text-white">{fmtMoney((rsum.manual_refund_due || 0) - (rsum.manual_refunded_amount || 0))}</span></div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {(rsum.stripe_refund_due || 0) - (rsum.stripe_refunded_amount || 0) > 0 && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/api/admin/installment-plans/${plan.id}/refund-stripe`, {}, { withCredentials: true });
                        toast.success("Reembolso Stripe enviado");
                        await reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erro Stripe");
                      }
                    }}
                    className="bg-sky-500 hover:bg-sky-600"
                    data-testid="plan-refund-stripe"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    Reembolsar via Stripe
                  </Button>
                )}
                {(rsum.manual_refund_due || 0) - (rsum.manual_refunded_amount || 0) > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/api/admin/installment-plans/${plan.id}/mark-manual-refunded`, {}, { withCredentials: true });
                        toast.success("Reembolso manual marcado");
                        await reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erro");
                      }
                    }}
                    className="border-white/10 text-zinc-200"
                    data-testid="plan-refund-manual"
                  >
                    Marcar manual reembolsado
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-2 border-t border-white/5">
            {plan.plan_status !== "cancelled" && plan.plan_status !== "completed" && remaining > 0 && (
              <Button onClick={() => setRecordingOpen(true)} className="btn-primary flex-1" data-testid="plan-record-payment-btn">
                <Plus className="w-4 h-4 mr-2" /> Registar prestação offline
              </Button>
            )}
            {plan.plan_status !== "cancelled" && plan.plan_status !== "completed" && plan.plan_status !== "frozen" && (
              <Button
                variant="outline"
                onClick={() => setExtendOpen(true)}
                className="border-sky-500/30 text-sky-200 hover:bg-sky-500/10"
                data-testid="plan-extend-deadline-btn"
              >
                <CalendarPlus className="w-4 h-4 mr-2" /> Dar mais dias
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setTimelineOpen(true)}
              className="border-white/10 text-zinc-200 hover:bg-white/5"
              data-testid="plan-timeline-btn"
            >
              <History className="w-4 h-4 mr-2" /> Ver histórico
            </Button>
            {plan.plan_status !== "cancelled" && (
              <Button variant="outline" onClick={() => setCancelOpen(true)} className="border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="plan-cancel-btn">
                <Ban className="w-4 h-4 mr-2" /> Cancelar plano
              </Button>
            )}
          </div>

          {(plan.payment_schedule || []).length > 0 && (
            <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3" data-testid="plan-schedule-view">
              <h4 className="text-sm font-semibold text-sky-200 mb-2 flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> Plano de pagamento acordado ({plan.payment_schedule.length} prestações)
                {plan.payment_agreement_signed && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Assinado</span>}
              </h4>
              <div className="space-y-1.5">
                {plan.payment_schedule.map((s) => (
                  <div key={s.id || s.sequence} className="grid grid-cols-[32px_1fr_90px_70px_28px] items-center gap-2 text-sm bg-white/[0.03] rounded px-3 py-1.5">
                    <span className="text-sky-300 font-semibold">#{s.sequence}</span>
                    <span className="text-zinc-200">{s.due_date ? new Date(s.due_date + "T00:00:00Z").toLocaleDateString("pt-PT") : "—"}</span>
                    <span className="text-amber-300 font-mono text-right">{Number(s.amount || 0).toFixed(2)}€</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded text-center ${s.paid ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-500/15 text-zinc-400"}`}>
                      {s.paid ? "Pago" : "Pendente"}
                    </span>
                    {!s.paid ? (
                      <button
                        type="button"
                        onClick={() => setEditingScheduleItem(s)}
                        className="text-sky-300 hover:text-sky-100"
                        title="Editar data ou valor"
                        data-testid={`plan-schedule-edit-${s.sequence}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                ))}
              </div>
              {plan.payment_agreement_document && (
                <a
                  href={`${API_URL}${plan.payment_agreement_document.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sky-300 hover:text-sky-200 text-xs underline"
                  data-testid="plan-agreement-doc-link"
                >
                  <Paperclip className="w-3.5 h-3.5" />
                  {plan.payment_agreement_document.filename}
                </a>
              )}
              {plan.payment_agreement_digital_signature && (
                <div className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5 text-[11px]" data-testid="plan-digital-signature">
                  <p className="text-emerald-200 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Assinado digitalmente por <strong>{plan.payment_agreement_digital_signature.typed_name}</strong>
                  </p>
                  <p className="text-zinc-500 pl-5">
                    {new Date(plan.payment_agreement_digital_signature.signed_at).toLocaleString("pt-PT")}
                    {plan.payment_agreement_digital_signature.ip_address && ` · IP ${plan.payment_agreement_digital_signature.ip_address}`}
                  </p>
                </div>
              )}
              {plan.payment_schedule_email_sent_at && (
                <p className="text-[10px] text-emerald-300 mt-1">
                  ✉️ Email enviado ao comprador em {new Date(plan.payment_schedule_email_sent_at).toLocaleString("pt-PT")}
                </p>
              )}
            </div>
          )}

          <ChangeRequestsSection
            plan={plan}
            onChanged={async () => { await reload(); onChanged && onChanged(); }}
          />

          {(plan.deadline_extensions || []).length > 0 && (
            <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3" data-testid="plan-extensions-history">
              <h4 className="text-sm font-semibold text-sky-200 mb-2 flex items-center gap-2">
                <CalendarPlus className="w-4 h-4" /> Extensões de prazo ({plan.deadline_extensions.length})
              </h4>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {plan.deadline_extensions.slice().reverse().map((ext) => (
                  <div key={ext.id || ext.at} className="text-xs bg-white/[0.03] rounded px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sky-200 font-semibold">+{ext.days} dia(s)</span>
                      <span className="text-zinc-500">{ext.at ? new Date(ext.at).toLocaleString("pt-PT") : ""}</span>
                    </div>
                    <p className="text-zinc-400">
                      Novo prazo: <span className="text-white">{ext.new_deadline ? new Date(ext.new_deadline).toLocaleDateString("pt-PT") : "—"}</span>
                      {ext.admin_email && <> · por <span className="text-zinc-300">{ext.admin_email}</span></>}
                    </p>
                    {ext.reason && <p className="text-zinc-400 mt-0.5 italic">&ldquo;{ext.reason}&rdquo;</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <RecordPaymentDialog
          open={recordingOpen}
          key={recordingOpen ? "rec-open" : "rec-closed"}
          onClose={() => setRecordingOpen(false)}
          planId={plan.id}
          remaining={remaining}
          schedule={plan.payment_schedule || []}
          onRecorded={async () => { setRecordingOpen(false); await reload(); onChanged && onChanged(); }}
        />
        <ExtendDeadlineDialog
          open={extendOpen}
          key={extendOpen ? "ext-open" : "ext-closed"}
          onClose={() => setExtendOpen(false)}
          planId={plan.id}
          onExtended={async () => { setExtendOpen(false); await reload(); onChanged && onChanged(); }}
        />
        <CancelPlanDialog
          open={cancelOpen}
          key={cancelOpen ? "cancel-open" : "cancel-closed"}
          onClose={() => setCancelOpen(false)}
          planId={plan.id}
          ticketType={data.ticket_type}
          onCancelled={async () => { setCancelOpen(false); await reload(); onChanged && onChanged(); }}
        />
        <EditScheduleItemDialog
          open={!!editingScheduleItem}
          item={editingScheduleItem}
          planId={plan.id}
          isSigned={!!plan.payment_agreement_digital_signature}
          onClose={() => setEditingScheduleItem(null)}
          onSaved={async () => { setEditingScheduleItem(null); await reload(); onChanged && onChanged(); }}
        />
        <PlanTimelineDialog
          open={timelineOpen}
          plan={plan}
          onClose={() => setTimelineOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};


const RecordPaymentDialog = ({ open, onClose, planId, remaining, schedule = [], onRecorded }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("offline");
  const [note, setNote] = useState("");
  const [scheduleItemId, setScheduleItemId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const unpaidItems = (schedule || []).filter((s) => !s.paid);

  const chooseScheduleItem = (id) => {
    setScheduleItemId(id);
    const item = unpaidItems.find((s) => s.id === id);
    if (item) setAmount(String(item.amount));
  };

  const submit = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return toast.error("Valor inválido");
    if (n > remaining + 0.005) return toast.error(`Excede o restante: ${remaining.toFixed(2)}€`);
    setSubmitting(true);
    try {
      const body = { amount: n, payment_method: method, note };
      if (scheduleItemId) body.schedule_item_id = scheduleItemId;
      const { data } = await axios.post(
        `${API_URL}/api/admin/installment-plans/${planId}/record-payment`,
        body,
        { withCredentials: true }
      );
      toast.success(data.plan_completed ? "Plano completo! QR emitido." : "Prestação registada.");
      onRecorded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-amber-500/30 text-white max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Registar prestação offline</DialogTitle>
          <DialogDescription className="text-zinc-400">Para pagamentos por transferência, multibanco, dinheiro, etc.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {unpaidItems.length > 0 && (
            <div>
              <Label className="text-zinc-300">Prestação do cronograma (opcional)</Label>
              <Select value={scheduleItemId || "none"} onValueChange={(v) => chooseScheduleItem(v === "none" ? "" : v)}>
                <SelectTrigger className="input-dark mt-1.5" data-testid="record-payment-schedule-item"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="none">— Sem associação —</SelectItem>
                  {unpaidItems.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      #{s.sequence} · {s.due_date ? new Date(s.due_date + "T00:00:00Z").toLocaleDateString("pt-PT") : "?"} · {Number(s.amount || 0).toFixed(2)}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-sky-300 mt-1">Ao escolher, o valor é preenchido automaticamente e a prestação fica marcada como paga.</p>
            </div>
          )}
          <div>
            <Label className="text-zinc-300">Valor (€)</Label>
            <Input type="number" step="0.01" min="0" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Máx ${remaining.toFixed(2)}€`} className="input-dark mt-1.5" data-testid="record-payment-amount" autoFocus />
          </div>
          <div>
            <Label className="text-zinc-300">Método</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="input-dark mt-1.5" data-testid="record-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                <SelectItem value="offline">Transferência / Multibanco</SelectItem>
                <SelectItem value="cash">Dinheiro</SelectItem>
                <SelectItem value="mbway">MB WAY</SelectItem>
                <SelectItem value="manual">Outro (Manual)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Nota (opcional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: NIB 1234, MB 9 8 7..." className="input-dark mt-1.5" data-testid="record-payment-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting || !amount} className="btn-primary" data-testid="record-payment-submit">{submitting ? "..." : "Registar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const ExtendDeadlineDialog = ({ open, onClose, planId, onExtended }) => {
  const [days, setDays] = useState("7");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  // Schedule (payment plan) state
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [scheduleCount, setScheduleCount] = useState(2);
  const [scheduleItems, setScheduleItems] = useState([]); // [{due_date, amount}]
  const [remaining, setRemaining] = useState(0);
  // Signed document upload
  const [docFile, setDocFile] = useState(null);
  const [existingDoc, setExistingDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Load plan + compute current deadline + prefill schedule
  useEffect(() => {
    if (!open || !planId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/installment-plans/${planId}`, { withCredentials: true });
        if (cancelled) return;
        const reg = data.plan || {};
        const sess = data.session || {};
        const tt = data.ticket_type || {};
        let currentDeadline = null;
        if (reg.deadline_override) {
          currentDeadline = new Date(reg.deadline_override);
        } else if (sess.date && (tt.installment_deadline_type || "before_session") !== "no_deadline") {
          const [y, m, d] = sess.date.split("-").map(Number);
          const [hh = 0, mm = 0] = (sess.time || "00:00").split(":").map(Number);
          const sessDt = new Date(Date.UTC(y, (m - 1), d, hh, mm));
          if (tt.installment_deadline_type === "until_session") {
            currentDeadline = sessDt;
          } else {
            const dDays = parseInt(tt.installment_deadline_days || 0, 10);
            currentDeadline = new Date(sessDt.getTime() - dDays * 24 * 3600 * 1000);
          }
        }
        const totalDue = Number(reg.total_due ?? reg.final_price ?? 0);
        const totalPaid = Number(reg.total_paid ?? 0);
        const rem = Math.max(0, Math.round((totalDue - totalPaid) * 100) / 100);
        setRemaining(rem);
        setPreview({ currentDeadline, extensions: (reg.deadline_extensions || []).length });
        // Preload existing schedule/agreement if any
        if (reg.payment_agreement_signed) setAgreementSigned(true);
        if (reg.payment_agreement_document) setExistingDoc(reg.payment_agreement_document);
        if ((reg.payment_schedule || []).length > 0) {
          setAgreementSigned(true);
          setScheduleCount(reg.payment_schedule.length);
          setScheduleItems(reg.payment_schedule.map((s) => ({
            due_date: s.due_date,
            amount: String(s.amount),
          })));
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [open, planId]);

  // Auto-generate default schedule (equal split, weekly) when count changes
  // AND user hasn't customised the rows yet.
  useEffect(() => {
    if (!agreementSigned) return;
    setScheduleItems((prev) => {
      // Keep existing rows if they already match the count and have data
      if (prev.length === scheduleCount && prev.every((r) => r.due_date && r.amount)) return prev;
      const n = scheduleCount;
      const per = remaining > 0 ? Math.floor((remaining / n) * 100) / 100 : 0;
      const last = Math.round((remaining - per * (n - 1)) * 100) / 100;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const rows = [];
      for (let i = 0; i < n; i++) {
        const d = new Date(today.getTime() + (i + 1) * 7 * 24 * 3600 * 1000);
        rows.push({
          due_date: d.toISOString().slice(0, 10),
          amount: String((i === n - 1 ? last : per).toFixed(2)),
        });
      }
      return rows;
    });
  }, [agreementSigned, scheduleCount, remaining]);

  const totalScheduled = scheduleItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const sumMismatch = agreementSigned && Math.abs(totalScheduled - remaining) > 0.01;

  const newDeadline = (() => {
    if (agreementSigned && scheduleItems.length > 0) {
      const dates = scheduleItems.map((r) => r.due_date).filter(Boolean).sort();
      const last = dates[dates.length - 1];
      return last ? new Date(last + "T00:00:00Z") : null;
    }
    const n = parseInt(days, 10);
    if (!n || n <= 0) return null;
    const now = new Date();
    const base = preview?.currentDeadline && preview.currentDeadline > now ? preview.currentDeadline : now;
    return new Date(base.getTime() + n * 24 * 3600 * 1000);
  })();

  const uploadDoc = async () => {
    if (!docFile) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      const { data } = await axios.post(
        `${API_URL}/api/admin/installment-plans/${planId}/agreement-document`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setExistingDoc(data.document);
      setDocFile(null);
      toast.success("Documento carregado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar documento");
    } finally { setUploadingDoc(false); }
  };

  const submit = async () => {
    if (agreementSigned && scheduleItems.length > 0) {
      if (sumMismatch) return toast.error(`Soma (${totalScheduled.toFixed(2)}€) ≠ Restante (${remaining.toFixed(2)}€)`);
      const invalid = scheduleItems.find((r) => !r.due_date || !parseFloat(r.amount));
      if (invalid) return toast.error("Preenche data e valor em todas as prestações");
    } else {
      const n = parseInt(days, 10);
      if (!n || n <= 0 || n > 365) return toast.error("Introduz entre 1 e 365 dias");
    }
    setSubmitting(true);
    try {
      const body = { reason: reason || null };
      if (agreementSigned) {
        body.agreement_signed = true;
        if (scheduleItems.length > 0) {
          body.payment_schedule = scheduleItems.map((r) => ({
            due_date: r.due_date,
            amount: parseFloat(r.amount),
          }));
        } else {
          body.days = parseInt(days, 10);
        }
      } else {
        body.days = parseInt(days, 10);
      }
      const { data } = await axios.post(
        `${API_URL}/api/admin/installment-plans/${planId}/extend-deadline`,
        body,
        { withCredentials: true },
      );
      toast.success(data.message || "Prazo estendido");
      onExtended();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao estender prazo");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-sky-500/30 text-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="extend-deadline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-sky-300" /> Dar mais dias ao comprador</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Estende o prazo de pagamento sem cancelar o plano. Se o plano estava &ldquo;Em atraso&rdquo;, volta a &ldquo;Em curso&rdquo;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {preview && (
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Prazo actual</p>
                  <p className="text-white font-mono">
                    {preview.currentDeadline ? preview.currentDeadline.toLocaleDateString("pt-PT") : "Sem prazo definido"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Em falta</p>
                  <p className="text-amber-300 font-mono font-bold">{remaining.toFixed(2)}€</p>
                </div>
              </div>
              {preview.extensions > 0 && (
                <p className="text-[11px] text-sky-300 mt-2">Este plano já foi estendido {preview.extensions} vez(es).</p>
              )}
            </div>
          )}

          {/* Simple days field (only if agreement not signed) */}
          {!agreementSigned && (
            <div>
              <Label className="text-zinc-300">Nº de dias a adicionar</Label>
              <Input
                type="number"
                min="1"
                max="365"
                step="1"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="input-dark mt-1.5"
                data-testid="extend-deadline-days"
                autoFocus
              />
              <p className="text-[11px] text-zinc-500 mt-1">Sugestões: 3, 7, 14, 30</p>
            </div>
          )}

          {/* Agreement checkbox */}
          <label className="flex items-start gap-3 bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 cursor-pointer hover:bg-sky-500/10 transition-colors" data-testid="extend-deadline-agreement-toggle">
            <input
              type="checkbox"
              checked={agreementSigned}
              onChange={(e) => setAgreementSigned(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-sky-500 cursor-pointer"
            />
            <div>
              <p className="text-sky-100 text-sm font-medium flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> Preencheu o Plano de Pagamento?
              </p>
              <p className="text-zinc-500 text-xs mt-0.5">
                Marca se combinaste um cronograma detalhado com o comprador. Podes definir 1 a 4 prestações com datas e valores próprios.
              </p>
            </div>
          </label>

          {/* Schedule editor */}
          {agreementSigned && (
            <div className="bg-white/[0.02] border border-sky-500/20 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sky-100">Número de prestações</Label>
                <div className="flex gap-1" data-testid="extend-deadline-schedule-count">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setScheduleCount(n)}
                      className={`w-9 h-9 rounded-lg border text-sm font-semibold transition-colors ${
                        scheduleCount === n
                          ? "bg-sky-500 border-sky-500 text-white"
                          : "bg-transparent border-white/10 text-zinc-300 hover:border-sky-500/40"
                      }`}
                      data-testid={`extend-deadline-count-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {scheduleItems.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[28px_1fr_100px] items-center gap-2" data-testid={`extend-deadline-row-${idx}`}>
                    <span className="text-sky-300 text-sm font-semibold">#{idx + 1}</span>
                    <Input
                      type="date"
                      value={row.due_date}
                      onChange={(e) => {
                        const v = e.target.value;
                        setScheduleItems((prev) => prev.map((r, i) => (i === idx ? { ...r, due_date: v } : r)));
                      }}
                      className="input-dark"
                      data-testid={`extend-deadline-date-${idx}`}
                    />
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          setScheduleItems((prev) => prev.map((r, i) => (i === idx ? { ...r, amount: v } : r)));
                        }}
                        className="input-dark pr-6"
                        data-testid={`extend-deadline-amount-${idx}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">€</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className={`flex items-center justify-between text-sm px-1 ${sumMismatch ? "text-red-300" : "text-emerald-300"}`}>
                <span>Total do cronograma</span>
                <span className="font-mono font-semibold" data-testid="extend-deadline-schedule-total">
                  {totalScheduled.toFixed(2)}€ / {remaining.toFixed(2)}€ {sumMismatch ? "⚠" : "✓"}
                </span>
              </div>

              {/* Signed document upload */}
              <div className="pt-2 border-t border-white/5">
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">Documento assinado (opcional)</Label>
                {existingDoc ? (
                  <div className="mt-2 flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                    <a
                      href={`${API_URL}${existingDoc.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-200 text-sm flex items-center gap-2 truncate hover:underline"
                      data-testid="extend-deadline-existing-doc"
                    >
                      <Paperclip className="w-4 h-4 shrink-0" />
                      <span className="truncate">{existingDoc.filename}</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => setExistingDoc(null)}
                      className="text-zinc-400 hover:text-red-300 shrink-0"
                      title="Remover para carregar outro"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2 items-center">
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      className="input-dark file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-white/10 file:text-zinc-200 file:text-xs"
                      data-testid="extend-deadline-doc-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={uploadDoc}
                      disabled={!docFile || uploadingDoc}
                      className="bg-sky-500 hover:bg-sky-600 shrink-0"
                      data-testid="extend-deadline-doc-upload"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      {uploadingDoc ? "..." : "Enviar"}
                    </Button>
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 mt-1">PDF, PNG, JPG ou WEBP · máx 8MB</p>
              </div>
            </div>
          )}

          {newDeadline && (
            <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3 text-sm">
              <p className="text-sky-200 text-xs uppercase tracking-wider mb-1">
                Novo prazo {agreementSigned && scheduleItems.length > 1 ? "(última prestação)" : ""}
              </p>
              <p className="text-white font-mono font-semibold" data-testid="extend-deadline-preview">
                {newDeadline.toLocaleDateString("pt-PT", { weekday: "short", day: "2-digit", month: "long", year: "numeric" })}
              </p>
              {agreementSigned && scheduleItems.length > 0 && !sumMismatch && (
                <p className="text-[11px] text-sky-200 mt-1">
                  ✉️ Será enviado email ao comprador com o cronograma completo.
                </p>
              )}
            </div>
          )}

          <div>
            <Label className="text-zinc-300">Motivo (opcional, fica no histórico)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Cliente prometeu pagar por MB Way até ao fim da semana"
              className="input-dark mt-1.5"
              data-testid="extend-deadline-reason"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={submitting || sumMismatch || (!agreementSigned && !days)}
            className="bg-sky-500 hover:bg-sky-600 text-white"
            data-testid="extend-deadline-submit"
          >
            {submitting ? "..." : agreementSigned && scheduleItems.length > 0 ? "Guardar cronograma" : "Estender prazo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const CancelPlanDialog = ({ open, onClose, planId, ticketType, onCancelled }) => {
  const [reason, setReason] = useState("");
  const [policy, setPolicy] = useState("");  // empty = use ticket type default
  const [feePct, setFeePct] = useState(ticketType?.cancellation_fee_percent ?? 10);
  const [submitting, setSubmitting] = useState(false);
  const effectivePolicy = policy || ticketType?.cancellation_policy || "partial_refund";

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { reason };
      if (policy) payload.override_policy = policy;
      if (effectivePolicy === "partial_refund") payload.cancellation_fee_percent = parseFloat(feePct) || 0;
      const { data } = await axios.post(
        `${API_URL}/api/admin/installment-plans/${planId}/cancel`,
        payload,
        { withCredentials: true }
      );
      toast.success("Plano cancelado");
      console.log("Cancel summary:", data);
      onCancelled();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent className="bg-zinc-900 border-red-500/30 text-white max-w-md" data-testid="cancel-plan-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Cancelar plano?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">
            O plano será cancelado e o lugar libertado (se estava reservado). O reembolso será calculado conforme a política.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-sm">Política de cancelamento</Label>
            <Select value={policy || "default"} onValueChange={(v) => setPolicy(v === "default" ? "" : v)}>
              <SelectTrigger className="input-dark mt-1.5" data-testid="cancel-policy"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                <SelectItem value="default">Padrão do tipo de bilhete ({ticketType?.cancellation_policy || "partial_refund"})</SelectItem>
                <SelectItem value="full_refund">Reembolso total</SelectItem>
                <SelectItem value="partial_refund">Reembolso parcial (com taxa)</SelectItem>
                <SelectItem value="forfeit">Sem reembolso</SelectItem>
                <SelectItem value="credit">Crédito para outro evento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {effectivePolicy === "partial_refund" && (
            <div>
              <Label className="text-zinc-300 text-sm">Taxa retida (%)</Label>
              <Input type="number" min="0" max="100" value={feePct} onChange={(e) => setFeePct(e.target.value)} className="input-dark mt-1.5" data-testid="cancel-fee-pct" />
            </div>
          )}
          <div>
            <Label className="text-zinc-300 text-sm">Razão</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Razão para o cancelamento..." className="input-dark mt-1.5" data-testid="cancel-reason" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white">Voltar</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={submitting} className="bg-red-500 hover:bg-red-600 text-white" data-testid="cancel-confirm">
            {submitting ? "..." : "Cancelar plano"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};



const EditScheduleItemDialog = ({ open, item, planId, isSigned, onClose, onSaved }) => {
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && item) {
      setDueDate(item.due_date || "");
      setAmount(String(item.amount || ""));
      setReason("");
    }
  }, [open, item]);

  if (!item) return null;
  const changed = dueDate !== item.due_date || parseFloat(amount) !== Number(item.amount);

  const submit = async () => {
    if (!changed) return toast.error("Nada foi alterado");
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/installment-plans/${planId}/schedule/${item.id}`,
        { due_date: dueDate, amount: parseFloat(amount), reason: reason || null },
        { withCredentials: true },
      );
      toast.success("Prestação actualizada");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-sky-500/30 text-white max-w-md" data-testid="edit-schedule-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-sky-300" /> Editar prestação #{item.sequence}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Nota: a soma de todas as prestações pendentes deve continuar a coincidir com o valor em falta.
          </DialogDescription>
        </DialogHeader>
        {isSigned && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-orange-200 text-xs">
            ⚠️ Este plano está assinado digitalmente. Ao guardar, a assinatura fica invalidada e o comprador terá de confirmar novamente.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300">Data</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input-dark mt-1.5" data-testid="edit-schedule-date" />
          </div>
          <div>
            <Label className="text-zinc-300">Valor (€)</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mt-1.5" data-testid="edit-schedule-amount" />
          </div>
          <div>
            <Label className="text-zinc-300">Motivo (opcional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Cliente atrasou salário" className="input-dark mt-1.5" data-testid="edit-schedule-reason" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting || !changed} className="bg-sky-500 hover:bg-sky-600" data-testid="edit-schedule-submit">
            {submitting ? "..." : "Guardar alteração"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const ChangeRequestsSection = ({ plan, onChanged }) => {
  const requests = (plan.schedule_change_requests || []).filter((r) => r.status === "pending" || r.status === "countered");
  const [busyId, setBusyId] = useState(null);
  const [counterOpen, setCounterOpen] = useState(null); // request being countered
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [counterDate, setCounterDate] = useState("");
  const [counterAmount, setCounterAmount] = useState("");
  const [counterReason, setCounterReason] = useState("");

  const respond = async (req, action, extras = {}) => {
    setBusyId(req.id);
    try {
      await axios.post(
        `${API_URL}/api/admin/installment-plans/${plan.id}/schedule/${req.item_id}/requests/${req.id}/respond`,
        { action, ...extras },
        { withCredentials: true },
      );
      toast.success(
        action === "accept" ? "Pedido aceite" :
        action === "reject" ? "Pedido rejeitado" :
        "Contra-proposta enviada",
      );
      setCounterOpen(null); setRejectOpen(null);
      setCounterDate(""); setCounterAmount(""); setCounterReason(""); setRejectReason("");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setBusyId(null); }
  };

  if (requests.length === 0) return null;
  const fmt = (iso) => iso ? new Date(iso + (iso.length === 10 ? "T00:00:00Z" : "")).toLocaleDateString("pt-PT") : "—";

  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3" data-testid="plan-change-requests">
      <h4 className="text-sm font-semibold text-amber-200 mb-2 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" /> Pedidos de alteração ({requests.length})
      </h4>
      <div className="space-y-3">
        {requests.map((req) => (
          <div key={req.id} className="bg-white/[0.03] rounded-lg p-3" data-testid={`request-${req.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <p className="text-white">
                  Prestação <strong className="text-sky-300">#{req.sequence}</strong>{" "}
                  <span className="text-zinc-500 text-xs">({req.requested_by_email})</span>
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  {req.proposed_date && (
                    <>Data: <s className="text-zinc-500">{fmt(req.current_due_date)}</s> → <strong className="text-amber-300">{fmt(req.proposed_date)}</strong></>
                  )}
                  {req.proposed_amount != null && (
                    <>{req.proposed_date ? " · " : ""}Valor: <s className="text-zinc-500">{Number(req.current_amount || 0).toFixed(2)}€</s> → <strong className="text-amber-300">{Number(req.proposed_amount || 0).toFixed(2)}€</strong></>
                  )}
                </p>
                {req.reason && <p className="text-xs text-zinc-400 italic mt-1">&ldquo;{req.reason}&rdquo;</p>}
                {req.status === "countered" && (
                  <p className="text-xs text-sky-300 mt-1">
                    Contra-proposta: {req.counter_date && <strong>{fmt(req.counter_date)}</strong>}{req.counter_amount != null && <> · <strong>{Number(req.counter_amount).toFixed(2)}€</strong></>}
                    <span className="text-zinc-500"> — a aguardar decisão do comprador</span>
                  </p>
                )}
              </div>
              {req.status === "pending" && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    disabled={busyId === req.id}
                    onClick={() => respond(req, "accept")}
                    className="bg-emerald-500 hover:bg-emerald-600 h-7 px-2 text-xs"
                    data-testid={`request-accept-${req.id}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Aceitar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === req.id}
                    onClick={() => { setCounterOpen(req); setCounterDate(req.proposed_date || ""); setCounterAmount(req.proposed_amount != null ? String(req.proposed_amount) : ""); }}
                    className="border-sky-500/40 text-sky-200 h-7 px-2 text-xs"
                    data-testid={`request-counter-${req.id}`}
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Contra-propor
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === req.id}
                    onClick={() => setRejectOpen(req)}
                    className="border-red-500/40 text-red-200 h-7 px-2 text-xs"
                    data-testid={`request-reject-${req.id}`}
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Rejeitar
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Counter dialog */}
      <Dialog open={!!counterOpen} onOpenChange={(v) => !v && setCounterOpen(null)}>
        <DialogContent className="bg-zinc-950 border-sky-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Contra-propor</DialogTitle>
            <DialogDescription className="text-zinc-400">Sugere uma alternativa. O comprador vai decidir se aceita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300">Data proposta</Label>
              <Input type="date" value={counterDate} onChange={(e) => setCounterDate(e.target.value)} className="input-dark mt-1.5" data-testid="counter-date" />
            </div>
            <div>
              <Label className="text-zinc-300">Valor proposto (opcional)</Label>
              <Input type="number" step="0.01" value={counterAmount} onChange={(e) => setCounterAmount(e.target.value)} className="input-dark mt-1.5" data-testid="counter-amount" />
            </div>
            <div>
              <Label className="text-zinc-300">Nota ao comprador</Label>
              <Input value={counterReason} onChange={(e) => setCounterReason(e.target.value)} placeholder="Ex: Posso ir até essa data mas com juro" className="input-dark mt-1.5" data-testid="counter-reason" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCounterOpen(null)}>Cancelar</Button>
            <Button
              onClick={() => respond(counterOpen, "counter", {
                counter_date: counterDate || null,
                counter_amount: counterAmount ? parseFloat(counterAmount) : null,
                admin_reason: counterReason || null,
              })}
              disabled={!counterDate && !counterAmount}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="counter-submit"
            >
              Enviar contra-proposta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={(v) => !v && setRejectOpen(null)}>
        <DialogContent className="bg-zinc-950 border-red-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeitar pedido</DialogTitle>
            <DialogDescription className="text-zinc-400">A prestação original (data e valor) mantém-se em vigor.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-zinc-300">Motivo (opcional)</Label>
            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex: Não podemos alterar por regra interna" className="input-dark mt-1.5" data-testid="reject-reason" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Voltar</Button>
            <Button
              onClick={() => respond(rejectOpen, "reject", { admin_reason: rejectReason || null })}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="reject-submit"
            >
              Rejeitar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};




const _timelineLabels = {
  created: "Criado",
  schedule_set: "Cronograma acordado",
  extension: "Prazo estendido",
  edit: "Prestação editada",
  request: "Pedido de alteração",
  request_response: "Resposta ao pedido",
  counter_accepted: "Contra-proposta aceite (buyer)",
  counter_rejected: "Contra-proposta recusada (buyer)",
  signed: "Cronograma assinado",
  prev_signed: "Assinatura anterior",
  sig_invalidated: "Assinatura invalidada",
  doc: "Documento carregado",
  payment: "Pagamento recebido",
  completed: "Plano concluído",
  cancelled: "Plano cancelado",
  frozen: "Plano congelado",
  overdue_flag: "Marcado em atraso",
};

const _sanitizeFilename = (s) => (s || "plano").replace(/[^a-zA-Z0-9_-]+/g, "_");

const exportTimelineExcel = (plan, events) => {
  const rows = events.map((e, i) => ({
    "#": i + 1,
    Data: e.at ? new Date(e.at).toLocaleString("pt-PT") : "",
    Tipo: _timelineLabels[e.type] || e.type,
    Título: e.title,
    Detalhe: e.detail || "",
  }));
  const header = [{
    "#": "Histórico do Plano",
    Data: plan.ticket_number || "",
    Tipo: `${plan.first_name || ""} ${plan.last_name || ""}`.trim(),
    Título: plan.email || "",
    Detalhe: `Exportado em ${new Date().toLocaleString("pt-PT")}`,
  }, {}];
  const ws = XLSX.utils.json_to_sheet([...header, ...rows], { skipHeader: false });
  ws["!cols"] = [{ wch: 6 }, { wch: 22 }, { wch: 26 }, { wch: 46 }, { wch: 60 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Historico");
  XLSX.writeFile(wb, `historico_${_sanitizeFilename(plan.ticket_number)}.xlsx`);
};

const exportTimelinePDF = (plan, events) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const buyerName = `${plan.first_name || ""} ${plan.last_name || ""}`.trim();
  doc.setFontSize(16); doc.text("Histórico do Plano de Pagamento", 40, 40);
  doc.setFontSize(10); doc.setTextColor(80);
  doc.text(`Bilhete: ${plan.ticket_number || "—"}`, 40, 60);
  doc.text(`Comprador: ${buyerName || "—"}${plan.email ? " · " + plan.email : ""}`, 40, 74);
  doc.text(`Total: ${Number(plan.total_due || plan.final_price || 0).toFixed(2)}€ · Pago: ${Number(plan.total_paid || 0).toFixed(2)}€ · Estado: ${plan.plan_status || plan.payment_status || "—"}`, 40, 88);
  doc.text(`Exportado em: ${new Date().toLocaleString("pt-PT")}`, 40, 102);
  autoTable(doc, {
    startY: 120,
    head: [["#", "Data", "Evento", "Detalhe"]],
    body: events.map((e, i) => [
      String(i + 1),
      e.at ? new Date(e.at).toLocaleString("pt-PT") : "",
      e.title || _timelineLabels[e.type] || e.type,
      e.detail || "",
    ]),
    styles: { fontSize: 9, cellPadding: 5, valign: "top" },
    headStyles: { fillColor: [24, 24, 27], textColor: 255 },
    columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 120 }, 2: { cellWidth: 160 }, 3: { cellWidth: "auto" } },
    theme: "grid",
  });
  doc.save(`historico_${_sanitizeFilename(plan.ticket_number)}.pdf`);
};


const PlanTimelineDialog = ({ open, plan, onClose }) => {
  const events = React.useMemo(() => {
    if (!plan) return [];
    const evs = [];
    // Plan created
    if (plan.created_at) {
      evs.push({
        type: "created", at: plan.created_at,
        icon: <CreditCard className="w-4 h-4" />, color: "amber",
        title: "Plano criado",
        detail: `Total: ${Number(plan.total_due || plan.final_price || 0).toFixed(2)}€ · ${plan.ticket_type_name || ""}`,
      });
    }
    // Schedule initially set
    if (plan.payment_schedule_set_at) {
      evs.push({
        type: "schedule_set", at: plan.payment_schedule_set_at,
        icon: <FileSignature className="w-4 h-4" />, color: "sky",
        title: `Cronograma acordado (${(plan.payment_schedule || []).length} prestações)`,
        detail: `Total: ${(plan.payment_schedule || []).reduce((s, r) => s + Number(r.amount || 0), 0).toFixed(2)}€`,
      });
    }
    // Extensions
    for (const ext of (plan.deadline_extensions || [])) {
      evs.push({
        type: "extension", at: ext.at,
        icon: <CalendarPlus className="w-4 h-4" />, color: "sky",
        title: `Prazo estendido em +${ext.days} dia(s)`,
        detail: `Novo prazo: ${ext.new_deadline ? new Date(ext.new_deadline).toLocaleDateString("pt-PT") : "—"}${ext.admin_email ? ` · por ${ext.admin_email}` : ""}${ext.reason ? ` · "${ext.reason}"` : ""}`,
      });
    }
    // Schedule edits
    for (const ed of (plan.schedule_edits || [])) {
      const dateChanged = ed.old_due_date !== ed.new_due_date;
      const amountChanged = Number(ed.old_amount || 0) !== Number(ed.new_amount || 0);
      const bits = [];
      if (dateChanged) bits.push(`data ${ed.old_due_date || "—"} → ${ed.new_due_date || "—"}`);
      if (amountChanged) bits.push(`valor ${Number(ed.old_amount || 0).toFixed(2)}€ → ${Number(ed.new_amount || 0).toFixed(2)}€`);
      evs.push({
        type: "edit", at: ed.at,
        icon: <Pencil className="w-4 h-4" />, color: "purple",
        title: `Prestação #${ed.sequence} editada${ed.source === "user_request_accepted" ? " (pedido aceite)" : ed.source === "counter_accepted" ? " (contra-proposta aceite)" : ""}`,
        detail: `${bits.join(" · ")}${ed.admin_email ? ` · por ${ed.admin_email}` : ed.user_email ? ` · por ${ed.user_email}` : ""}${ed.reason ? ` · "${ed.reason}"` : ""}`,
      });
    }
    // Change requests (all statuses for full audit)
    for (const req of (plan.schedule_change_requests || [])) {
      evs.push({
        type: "request", at: req.requested_at,
        icon: <MessageSquare className="w-4 h-4" />, color: "amber",
        title: `Pedido de alteração da prestação #${req.sequence}`,
        detail: `${req.requested_by_email || ""}${req.proposed_date ? ` · data → ${req.proposed_date}` : ""}${req.proposed_amount != null ? ` · valor → ${Number(req.proposed_amount).toFixed(2)}€` : ""}${req.reason ? ` · "${req.reason}"` : ""}`,
      });
      if (req.admin_at) {
        const label = req.status === "rejected" ? "Pedido rejeitado pelo admin"
                    : req.status === "countered" ? "Admin contra-propôs"
                    : req.status === "accepted" ? "Pedido aceite pelo admin" : `Estado: ${req.status}`;
        const color = req.status === "rejected" ? "red" : req.status === "accepted" ? "emerald" : "sky";
        const bits = [];
        if (req.counter_date) bits.push(`data ${req.counter_date}`);
        if (req.counter_amount != null) bits.push(`valor ${Number(req.counter_amount).toFixed(2)}€`);
        evs.push({
          type: "request_response", at: req.admin_at,
          icon: req.status === "rejected" ? <XCircle className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />,
          color,
          title: label,
          detail: `${bits.length ? bits.join(" · ") + " · " : ""}${req.admin_email || ""}${req.admin_reason ? ` · "${req.admin_reason}"` : ""}`,
        });
      }
      if (req.user_decided_at && req.status === "accepted") {
        evs.push({
          type: "counter_accepted", at: req.user_decided_at,
          icon: <Check className="w-4 h-4" />, color: "emerald",
          title: "Comprador aceitou contra-proposta",
          detail: req.requested_by_email || "",
        });
      }
      if (req.user_decided_at && req.status === "rejected" && req.user_final_decision === "rejected_counter") {
        evs.push({
          type: "counter_rejected", at: req.user_decided_at,
          icon: <XCircle className="w-4 h-4" />, color: "red",
          title: "Comprador recusou contra-proposta",
          detail: req.requested_by_email || "",
        });
      }
    }
    // Signatures
    const sig = plan.payment_agreement_digital_signature;
    if (sig?.signed_at) {
      evs.push({
        type: "signed", at: sig.signed_at,
        icon: <CheckCircle2 className="w-4 h-4" />, color: "emerald",
        title: "Cronograma assinado digitalmente",
        detail: `${sig.typed_name || sig.signed_by_email || ""}${sig.ip_address ? ` · IP ${sig.ip_address}` : ""}`,
      });
    }
    for (const prev of (plan.payment_agreement_previous_signatures || [])) {
      if (prev.signed_at) {
        evs.push({
          type: "prev_signed", at: prev.signed_at,
          icon: <FileSignature className="w-4 h-4" />, color: "zinc",
          title: "Assinatura anterior",
          detail: `${prev.typed_name || prev.signed_by_email || ""}${prev.ip_address ? ` · IP ${prev.ip_address}` : ""}`,
        });
      }
      if (prev.invalidated_at) {
        evs.push({
          type: "sig_invalidated", at: prev.invalidated_at,
          icon: <AlertTriangle className="w-4 h-4" />, color: "orange",
          title: "Assinatura invalidada",
          detail: `${prev.reason || ""}${prev.invalidated_by ? ` · por ${prev.invalidated_by}` : ""}`,
        });
      }
    }
    // Agreement document
    if (plan.payment_agreement_document?.uploaded_at) {
      evs.push({
        type: "doc", at: plan.payment_agreement_document.uploaded_at,
        icon: <Paperclip className="w-4 h-4" />, color: "sky",
        title: "Documento assinado carregado",
        detail: `${plan.payment_agreement_document.filename}${plan.payment_agreement_document.uploaded_by ? ` · por ${plan.payment_agreement_document.uploaded_by}` : ""}`,
      });
    }
    // Payments (installments)
    for (const inst of (plan.installments || [])) {
      evs.push({
        type: "payment", at: inst.paid_at,
        icon: <Wallet className="w-4 h-4" />, color: "emerald",
        title: `Pagamento recebido +${Number(inst.amount).toFixed(2)}€`,
        detail: `${inst.payment_method || "—"}${inst.note ? ` · ${inst.note}` : ""}${inst.recorded_by_admin_id ? " · registado pelo admin" : ""}`,
      });
    }
    // Plan completed / cancelled / frozen
    if (plan.plan_status === "completed" && plan.qr_issued) {
      evs.push({
        type: "completed", at: plan.created_at, // fallback if no explicit ts
        icon: <CheckCircle2 className="w-4 h-4" />, color: "emerald",
        title: "Plano concluído · QR emitido",
        detail: `Total pago: ${Number(plan.total_paid || 0).toFixed(2)}€`,
      });
    }
    if (plan.cancelled_at) {
      evs.push({
        type: "cancelled", at: plan.cancelled_at,
        icon: <Ban className="w-4 h-4" />, color: "red",
        title: "Plano cancelado",
        detail: plan.cancellation_reason || "",
      });
    }
    if (plan.frozen_at) {
      evs.push({
        type: "frozen", at: plan.frozen_at,
        icon: <Clock className="w-4 h-4" />, color: "sky",
        title: "Plano congelado",
        detail: plan.cancellation_reason || "",
      });
    }
    if (plan.overdue_flagged_at) {
      evs.push({
        type: "overdue_flag", at: plan.overdue_flagged_at,
        icon: <AlertTriangle className="w-4 h-4" />, color: "orange",
        title: "Marcado como em atraso",
        detail: "",
      });
    }
    // Sort ascending by time (oldest first for readability)
    evs.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
    return evs;
  }, [plan]);

  if (!plan) return null;

  const colorMap = {
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    purple: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    zinc: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-amber-300" /> Histórico completo · {plan.ticket_number}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Timeline com {events.length} evento(s) — cronograma, assinaturas, pedidos, pagamentos e estado.
          </DialogDescription>
        </DialogHeader>
        {events.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportTimelineExcel(plan, events)}
              className="border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10"
              data-testid="timeline-export-excel"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportTimelinePDF(plan, events)}
              className="border-red-500/30 text-red-200 hover:bg-red-500/10"
              data-testid="timeline-export-pdf"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
          </div>
        )}
        {events.length === 0 ? (
          <p className="text-zinc-500 text-sm py-8 text-center">Sem eventos registados.</p>
        ) : (
          <ol className="relative border-l-2 border-white/10 ml-4 space-y-4 pl-6 py-2">
            {events.map((e, i) => (
              <li key={i} className="relative" data-testid={`timeline-event-${e.type}-${i}`}>
                <span className={`absolute -left-[34px] top-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${colorMap[e.color] || colorMap.zinc}`}>
                  {e.icon}
                </span>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{e.title}</p>
                  {e.detail && <p className="text-zinc-400 text-xs mt-0.5">{e.detail}</p>}
                  <p className="text-zinc-600 text-[10px] mt-1 uppercase tracking-wider">
                    {e.at ? new Date(e.at).toLocaleString("pt-PT") : "—"}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

